import { useState, useEffect } from 'react';
import { Bell, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

export function NotificationsSettingsView() {
  const { profile } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [notifications, setNotifications] = useState({
    newLoad: true,
    newDispatch: true,
    dispatchStatusChange: true,
    driverSmsMessage: true,
    newDriverAdded: true,
    newTruckAdded: true,
    newTrailerAdded: true,
    documentUploaded: true,
    maintenanceDue: true,
    maintenanceCompleted: true,
    inspectionDue: true,
    accidentReported: true,
    loadReadyBilling: true,
    invoiceCreated: true,
    paymentReceived: true,
    driverAvailable: true,
    driverUnavailable: true,
  });

  const [quietHours, setQuietHours] = useState({
    enabled: false,
    start: '22:00',
    end: '07:00',
  });

  useEffect(() => {
    if (profile?.id) {
      loadPreferences();
    }
  }, [profile]);

  const loadPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', profile?.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setNotifications({
          newLoad: data.new_load,
          newDispatch: data.new_dispatch,
          dispatchStatusChange: data.dispatch_status_change,
          driverSmsMessage: data.driver_sms_message,
          newDriverAdded: data.new_driver_added,
          newTruckAdded: data.new_truck_added,
          newTrailerAdded: data.new_trailer_added,
          documentUploaded: data.document_uploaded,
          maintenanceDue: data.maintenance_due,
          maintenanceCompleted: data.maintenance_completed,
          inspectionDue: data.inspection_due,
          accidentReported: data.accident_reported,
          loadReadyBilling: data.load_ready_billing,
          invoiceCreated: data.invoice_created,
          paymentReceived: data.payment_received,
          driverAvailable: data.driver_available,
          driverUnavailable: data.driver_unavailable,
        });

        setQuietHours({
          enabled: data.quiet_hours_enabled || false,
          start: data.quiet_hours_start?.substring(0, 5) || '22:00',
          end: data.quiet_hours_end?.substring(0, 5) || '07:00',
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: profile?.id,
          new_load: notifications.newLoad,
          new_dispatch: notifications.newDispatch,
          dispatch_status_change: notifications.dispatchStatusChange,
          driver_sms_message: notifications.driverSmsMessage,
          new_driver_added: notifications.newDriverAdded,
          new_truck_added: notifications.newTruckAdded,
          new_trailer_added: notifications.newTrailerAdded,
          document_uploaded: notifications.documentUploaded,
          maintenance_due: notifications.maintenanceDue,
          maintenance_completed: notifications.maintenanceCompleted,
          inspection_due: notifications.inspectionDue,
          accident_reported: notifications.accidentReported,
          load_ready_billing: notifications.loadReadyBilling,
          invoice_created: notifications.invoiceCreated,
          payment_received: notifications.paymentReceived,
          driver_available: notifications.driverAvailable,
          driver_unavailable: notifications.driverUnavailable,
          quiet_hours_enabled: quietHours.enabled,
          quiet_hours_start: quietHours.start + ':00',
          quiet_hours_end: quietHours.end + ':00',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast.success('Notification preferences saved successfully!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const notificationTypes = [
    { id: 'newLoad', label: 'New Load Created', description: 'When a new load is created in the system' },
    { id: 'newDispatch', label: 'New Dispatch Created', description: 'When a dispatch is created for a load' },
    { id: 'dispatchStatusChange', label: 'Dispatch Status Updates', description: 'When dispatch status changes' },
    { id: 'driverSmsMessage', label: 'Driver SMS Messages', description: 'SMS messages received from drivers' },
    { id: 'newDriverAdded', label: 'New Driver Added', description: 'When a new driver joins the fleet' },
    { id: 'newTruckAdded', label: 'New Truck Added', description: 'When a new truck is added to fleet' },
    { id: 'newTrailerAdded', label: 'New Trailer Added', description: 'When a new trailer is added to fleet' },
    { id: 'documentUploaded', label: 'Document Uploaded', description: 'BOL, POD, and other documents' },
    { id: 'maintenanceDue', label: 'Maintenance Due', description: 'Vehicle maintenance is due' },
    { id: 'maintenanceCompleted', label: 'Maintenance Completed', description: 'Vehicle maintenance completed' },
    { id: 'inspectionDue', label: 'Inspection Due', description: 'Vehicle inspection is due' },
    { id: 'accidentReported', label: 'Accident Reported', description: 'When an accident is reported' },
    { id: 'loadReadyBilling', label: 'Load Ready for Billing', description: 'Loads ready for invoicing' },
    { id: 'invoiceCreated', label: 'Invoice Created', description: 'When an invoice is created' },
    { id: 'paymentReceived', label: 'Payment Received', description: 'When payment is received' },
    { id: 'driverAvailable', label: 'Driver Available', description: 'Driver becomes available' },
    { id: 'driverUnavailable', label: 'Driver Unavailable', description: 'Driver becomes unavailable' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-orange-100 p-3 rounded-lg">
            <Bell className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Notification Preferences</h2>
            <p className="text-slate-600">Choose how you want to be notified about important events</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-4 px-4 text-sm font-semibold text-slate-700">Notification Type</th>
                <th className="text-center py-4 px-4 text-sm font-semibold text-slate-700">
                  <div className="flex flex-col items-center gap-1">
                    <Bell className="w-5 h-5 text-purple-600" />
                    <span>Push Notifications</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {notificationTypes.map((type) => (
                <tr key={type.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-4 px-4">
                    <div>
                      <p className="font-medium text-slate-900">{type.label}</p>
                      <p className="text-sm text-slate-500">{type.description}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={notifications[type.id as keyof typeof notifications]}
                        onChange={(e) =>
                          setNotifications({
                            ...notifications,
                            [type.id]: e.target.checked,
                          })
                        }
                      />
                      <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t border-slate-200 mt-6">
          <button
            onClick={savePreferences}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Preferences
              </>
            )}
          </button>
        </div>
      </div>

      {/* Quiet Hours */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Quiet Hours</h3>
        <p className="text-sm text-slate-600 mb-4">
          Disable notifications during specific hours (except urgent/critical alerts)
        </p>
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">From</label>
            <input
              type="time"
              value={quietHours.start}
              onChange={(e) => setQuietHours({ ...quietHours, start: e.target.value })}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">To</label>
            <input
              type="time"
              value={quietHours.end}
              onChange={(e) => setQuietHours({ ...quietHours, end: e.target.value })}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={quietHours.enabled}
                onChange={(e) => setQuietHours({ ...quietHours, enabled: e.target.checked })}
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-3 text-sm font-medium text-slate-700">Enable</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
